import React, { useEffect, useState } from "react";
import { APP_CONFIG } from "../../../../constants/constants";
import {
  CameraListingPayload,
  DeviceListingResponse,
} from "../../../../api/types/Camera";
import { useGetDeviceListingQuery } from "../../../../api/network/camerasAdminVMApiService";
import { useTranslation } from "react-i18next";
import AppSearchBox from "../../../../components/searchBox";
import { useDebouncedCallback } from "use-debounce";
import CamerIcon from "../../../../assets/svg/camera-icon.svg";
import Pagination, {
  TPaginationSelected,
} from "../../../../components/pagination";
import { TSelectboxOption } from "../../../../components/admin/formFields";
import TimelineSelector from "./timeline-selector";
import { consistentDisplayAssetItem } from "../../../../utils/vehicle";
import { useLoggedInUserData } from "../../../../utils/user";

const ScreenVideoPlayback = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.video_history.video_playback",
  });

  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";
  const [cameraManagementQueryParams, setCameraManagementQueryParams] =
    useState<CameraListingPayload>({
      keyword: "",
      model: "",
      status: "",
      offset: 0,
      limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
    });
  const [cameraId, setCameraId] = useState<string | null>(null);
  const [serialNum, setSerialNum] = useState<string | null>(null);
  const {
    data: dataDeviceListing,
    isFetching: isFetchingDeviceListing,
    error,
    refetch,
  } = useGetDeviceListingQuery(
    {
      ...cameraManagementQueryParams,
      //search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.ALL_FLAG, // TODO: search_fields to be implemented for camera listing API
      offset: cameraManagementQueryParams.offset,
      // (cameraManagementQueryParams.page - 1) *
      // cameraManagementQueryParams.limit,
    },
    {
      refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
    }
  );

  const { count, data: results } = dataDeviceListing || {};
  useEffect(() => {
    if (results?.length) {
      setCameraId(results?.[0]?.cid);
      setSerialNum(results?.[0]?.serial_number);
    }
  }, [results]);
  // prepare query params for fetching organization users
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    setCameraManagementQueryParams({
      ...cameraManagementQueryParams,
      offset: 0,
      keyword: value,
    });
  }, 500);
  return (
    <div className="p-2 bg-gray-100 rounded-lg grid grid-cols-12 gap-2">
      {/* Listing */}
      <div
        className={`col-span-3 bg-white rounded-lg p-2 space-y-4 ${isFetchingDeviceListing ? " opacity-40 pointer-events-none" : ""}`}
      >
        <div className="font-semibold text-sm leading-none tracking-normal mt-3">
          {t("listing_heading")}
        </div>
        <AppSearchBox
          placeholder={t("search_placeholder_listing")}
          initialValue={cameraManagementQueryParams?.keyword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          onClear={() => {
            debouncedSetSearchKeyword("");
          }}
        />
        <div className="max-h-admins overflow-y-auto">
          {results?.map((item: DeviceListingResponse, index: number) => {
            return (
              <div
                key={item.serial_number}
                className={`w-full flex gap-2 items-center border-b p-3 border-gray-200 ${cameraId === item?.cid ? "bg-accent-blue-paleO66" : "hover:bg-accent-blue-paleO66 cursor-pointer"}`}
                onClick={() => {
                  setCameraId(item?.cid);
                  setSerialNum(item?.serial_number);
                }}
              >
                <img src={CamerIcon} alt="camera" className="" />
                <p className="font-normal text-xs tracking-normal">
                  {`${consistentDisplayAssetItem(item.ft_device_id, item.ft_device_description, item.ft_displayname, item.drivername, showDriver)}`}
                </p>
              </div>
            );
          })}
        </div>
        {!isFetchingDeviceListing && (
          <Pagination
            pageSize={cameraManagementQueryParams.limit}
            handlePageSizeChange={(e: TSelectboxOption | null) => {
              setCameraManagementQueryParams({
                ...cameraManagementQueryParams,
                offset: 0,
                limit: parseInt(`${e?.value}`),
              });
            }}
            totalPages={
              count ? Math.ceil(count / cameraManagementQueryParams.limit) : 1
            }
            forcePage={
              !!cameraManagementQueryParams.offset
                ? Math.floor(
                    cameraManagementQueryParams.offset /
                      cameraManagementQueryParams.limit
                  )
                : 0
            }
            handlePageClick={(data: TPaginationSelected) => {
              setCameraManagementQueryParams({
                ...cameraManagementQueryParams,
                offset: data.selected * cameraManagementQueryParams.limit,
              });
            }}
            onlyPageChange={true}
            size={1}
          />
        )}
      </div>
      {/* Timeline */}
      <div className="col-span-9 gap-2">
        {cameraId !== "" && (
          <TimelineSelector
            cameraId={cameraId ?? ""}
            serial_num={serialNum ?? ""}
          />
        )}
      </div>
    </div>
  );
};

export default ScreenVideoPlayback;
